import { useState, useMemo } from 'react';
import { MarkdownPreview } from './MarkdownPreview';
import type { ChapterEdit, ChapterUpdate } from '@/types';

interface ChapterEditViewProps {
  chapter: ChapterEdit;
  totalChapters: number;
  onSave: (updates: ChapterUpdate) => void;
  onCancel: () => void;
  onPrevChapter?: () => void;
  onNextChapter?: () => void;
}

export function ChapterEditView({
  chapter,
  totalChapters,
  onSave,
  onCancel,
  onPrevChapter,
  onNextChapter
}: ChapterEditViewProps) {
  const [title, setTitle] = useState(chapter.title);
  const [tokenCost, setTokenCost] = useState(chapter.tokenCost);
  const [content, setContent] = useState(chapter.content);
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');

  const hasChanges = useMemo(() => (
    title !== chapter.title ||
    tokenCost !== chapter.tokenCost ||
    content !== chapter.content
  ), [title, tokenCost, content, chapter.content, chapter.title, chapter.tokenCost]);

  const handleSave = () => {
    onSave({
      title: title.trim(),
      tokenCost,
      content: content.trim()
    });
  };

  const handleCancel = () => {
    if (hasChanges && !confirm('You have unsaved changes. Discard them?')) {
      return;
    }
    onCancel();
  };

  // Format read time
  const formatReadTime = (minutes: number) => {
    if (!minutes || minutes < 60) return `${minutes || 0}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <span className="text-sm text-stone-400">
            Chapter {chapter.sequence} of {totalChapters}
          </span>
          
          <div className="flex items-center gap-1">
            <button
              onClick={onPrevChapter}
              disabled={chapter.sequence <= 1}
              className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Previous chapter"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
            
            <button
              onClick={onNextChapter}
              disabled={chapter.sequence >= totalChapters}
              className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Next chapter"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-stone-600 hover:text-stone-800 transition-colors"
          >
            Cancel
          </button>
          
          <button
            onClick={handleSave}
            disabled={!hasChanges || !title.trim()}
            className="px-6 py-2 bg-stone-800 text-white rounded-lg hover:bg-stone-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Save Chapter
          </button>
        </div>
      </div>

      {/* Chapter Metadata */}
      <div className="bg-white rounded-lg border border-stone-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-stone-600 mb-2">
              Chapter Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-stone-200 rounded-lg text-stone-800 placeholder-stone-300 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
              placeholder="Chapter title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-600 mb-2">
              Token Cost
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="0"
                value={tokenCost}
                onChange={(e) => setTokenCost(parseInt(e.target.value) || 0)}
                className="w-32 px-4 py-2 border border-stone-200 rounded-lg text-stone-800 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
              />
              <span className="text-sm text-stone-500">
                {tokenCost === 0 ? '(Free chapter)' : 'tokens'}
              </span>
            </div>
          </div>
        </div>

        {/* Chapter Stats */}
        {(chapter.wordCount !== undefined || chapter.readTime !== undefined || chapter.llmTokens !== undefined) && (
          <div className="mt-6 pt-6 border-t border-stone-100">
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-3">Chapter Stats</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-stone-50 rounded-lg p-3">
                <p className="text-lg font-semibold text-stone-800">{(chapter.wordCount || 0).toLocaleString()}</p>
                <p className="text-xs text-stone-400">words</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3">
                <p className="text-lg font-semibold text-stone-800">{formatReadTime(chapter.readTime || 0)}</p>
                <p className="text-xs text-stone-400">read time</p>
              </div>
              <div className="bg-stone-50 rounded-lg p-3">
                <p className="text-lg font-semibold text-stone-800">{(chapter.llmTokens || 0).toLocaleString()}</p>
                <p className="text-xs text-stone-400">LLM tokens</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Content Editor with Tabs */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-stone-600">
            Chapter Content
          </label>
          <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('edit')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'edit'
                  ? 'bg-white text-stone-800 shadow-sm'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              Edit
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                activeTab === 'preview'
                  ? 'bg-white text-stone-800 shadow-sm'
                  : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              Preview
            </button>
          </div>
        </div>
        
        {activeTab === 'edit' ? (
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your chapter content here... Use **bold**, *italic*, [links](https://example.com)"
            className="flex-1 w-full px-4 py-4 border border-stone-200 rounded-lg text-stone-700 placeholder-stone-300 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 resize-none font-mono text-sm leading-relaxed"
          />
        ) : (
          <div className="flex-1 w-full px-4 py-4 bg-stone-50 rounded-lg border border-stone-200 overflow-auto">
            <MarkdownPreview markdown={content} />
          </div>
        )}
        
        <div className="mt-2 text-xs text-stone-400">
          {content.length.toLocaleString()} characters
        </div>
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700">
            You have unsaved changes. Click "Save Chapter" to save them.
          </p>
        </div>
      )}
    </div>
  );
}
